import { useState, useEffect, useCallback, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Calendar,
  Typography,
  Space,
  Select,
  Button,
  Tooltip
} from "antd";
import {
  ArrowRightOutlined,
  SyncOutlined,
  PlusCircleOutlined,
  UnorderedListOutlined
} from "@ant-design/icons";
import UserManageTripReqPage from "./UserManageTripReqPage";
import UserTripListModal from "../../../components/Ouber/modals/user/UserTripListModal";
import UserViewTripModal from "../../../components/Ouber/modals/user/UserViewTripModal";
import UserViewTripReqModal from "../../../components/Ouber/modals/user/UserViewTripReqModal";
import UserCancelConfirmModal from "../../../components/Ouber/modals/user/UserCancelConfirmModal";
import UserRequestTripModal from "../../../components/Ouber/modals/user/UserRequestTripModal";
import OuberPageInfoIcon from "../../../components/Ouber/OuberPageInfoIcon";
import { useOuberService } from "../../../services";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";
import { officeAcronyms, getCalendarWeekCount } from "../../../utils/ouberUtils";
import { useSelector } from "react-redux";
dayjs.extend(customParseFormat);

/**
 * The purpose of this page is to provide regular employees with a 
 * calendar-based interface to browse, join, or request new trips.
 */
const UserCalendarOverviewPage = () => {
  const currentUser = useSelector((state) => state.accountDetailSlice?.details);
  const employeeId = currentUser?.employee_id;
  const hasActivePosition = (currentUser?.positions || []).some(
    (position) => !position?.is_inactive,
  );
  const noPositionMessage =
    "You need an assigned position before you can request a trip. Please contact your Positions Admin.";

  const [activeView, setActiveView] = useState('calendar'); // 'calendar' | 'trips'
  const [currentDate, setCurrentDate] = useState(dayjs());
  const calendarWeekCount = getCalendarWeekCount(currentDate);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedDateTrips, setSelectedDateTrips] = useState([]);
  
  const [isTripListModalVisible, setIsTripListModalVisible] = useState(false);
  const [isViewTripModalVisible, setIsViewTripModalVisible] = useState(false);
  const [isViewReqModalVisible, setIsViewReqModalVisible] = useState(false);
  const [isRequestTripModalVisible, setIsRequestTripModalVisible] = useState(false);
  const [isCancelConfirmModalVisible, setIsCancelConfirmModalVisible] = useState(false);
  const [tripRequestIdToCancel, setTripRequestIdToCancel] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { service: ouberService } = useOuberService();
  const [tripData, setTripData] = useState([]);
  const [unitFilter, setUnitFilter] = useState('');

  const filteredData = tripData; // Using tripData directly as it is filtered by the backend


  // Fetches active trips and the user's specific trip requests from the service
  const loadTrips = useCallback(async (unit = '') => {
    if (!currentUser) return;
    try {
      const params = unit ? { unit } : {};
      const [tripsResponse, reqResponse] = await Promise.all([
        ouberService.getTrips(params),
        ouberService.getTripRequests()
      ]);

      const tripsListRaw = Array.isArray(tripsResponse) ? tripsResponse : (tripsResponse?.data || []);
      const tripsList = tripsListRaw.filter(t => t && t.trip_status !== 'Cancelled by AO');
      const requestsListRaw = Array.isArray(reqResponse) ? reqResponse : (reqResponse?.data || []);
      const requestsList = requestsListRaw.filter(r => r !== null && r !== undefined);
      const myId = Number(employeeId || currentUser?.id);

      // Formats raw trip data into a UI-friendly structure for the calendar
      const trips = tripsList.map(t => ({
        ...t,
        key: 'trip_' + (t.trip_id || Math.random()),
        id: (t.trip_id || "").toString(),
        pickup: t.pickup_venue,
        destination: t.dropoff_location,
        date: t.trip_date ? t.trip_date.substring(0, 10) : null,
        time: t.pickup_time ? (() => { const h = parseInt(t.pickup_time.split(":")[0]); return (h % 12 || 12) + ":" + t.pickup_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : null,
        estimatedArrival: t.estimated_arrival_time ? (() => { const h = parseInt(t.estimated_arrival_time.split(":")[0]); return (h % 12 || 12) + ":" + t.estimated_arrival_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : null,
        status: t.trip_status,
        driverName: (t.driver && typeof t.driver === 'object' && t.driver.driver_name) ? t.driver.driver_name : (t.custom_driver_name || "Unassigned"),
        driverContact: (t.driver && typeof t.driver === 'object' && t.driver.contact_number) ? t.driver.contact_number : (t.custom_driver_contact || "N/A"),
        driver: t.driver,
        vehicle: (t.vehicle && typeof t.vehicle === 'object' && t.vehicle.vehicle_model) ? t.vehicle.vehicle_model : (t.custom_vehicle_name || "Unassigned"),
        vehicleType: (t.vehicle && typeof t.vehicle === 'object' && t.vehicle.vehicle_type) ? t.vehicle.vehicle_type : (t.custom_vehicle_type || "N/A"),
        plateNumber: (t.vehicle && typeof t.vehicle === 'object' && t.vehicle.plate_number) ? t.vehicle.plate_number : (t.custom_vehicle_plate || "N/A"),
        capacity: `${t.passengers ? t.passengers.length : 0}/${t.trip_passenger_capacity}`,
        isMultiplePickupAllowed: !!t.allowed_multiple_pickup,
        allowed_multiple_pickup: t.allowed_multiple_pickup,
        purpose: t.trip_purpose,
        organizing_unit: t.organizing_unit,
        isUnlinkedRequest: false
      }));

      const myUnlinkedReqs = requestsList
        .filter(r => r && !r.trip_id && Number(r.employee_id) === myId && !['Cancelled by AO', 'Cancelled by User', 'Declined by AO'].includes(r.request_status))
        .map(r => ({
          key: 'req_' + (r.trip_request_id || Math.random()),
          id: (r.trip_request_id || "").toString(),
          pickup: r.pickup_venue,
          destination: r.dropoff_location,
          date: r.trip_date ? r.trip_date.substring(0, 10) : null,
          time: r.estimated_pickup_time ? (() => { const h = parseInt(r.estimated_pickup_time.split(":")[0]); return (h % 12 || 12) + ":" + r.estimated_pickup_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : "TBD",
          status: r.request_status,
          purpose: r.trip_purpose,
          organizing_unit: r.organizing_unit || r.employee?.positions?.[0]?.unit || r.employee?.unit || r.unit,
          isUnlinkedRequest: true,
          rawData: r
        }));

      setTripData([...trips, ...myUnlinkedReqs]);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load active user trips.");
    }
  }, [ouberService, currentUser, employeeId]);

  const loadTripsRef = useRef(loadTrips);
  useEffect(() => {
    loadTripsRef.current = loadTrips;
  }, [loadTrips]);

  // Triggers the loadTrips fetch with a debounce delay to handle live unit searching
  useEffect(() => {
    const handler = setTimeout(() => {
      loadTripsRef.current(unitFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [unitFilter]);

  // Fetch data immediately when the window regains focus to keep legends accurate across different accounts
  useEffect(() => {
    const handleFocus = () => {
      loadTripsRef.current(unitFilter);
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [unitFilter]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadTrips(unitFilter);
    } finally {
      setRefreshing(false);
    }
  }, [loadTrips, unitFilter]);

  /* --- MODAL HANDLERS --- */
  const handleDayClick = useCallback((date) => {
    const dateString = date.format("YYYY-MM-DD");
    const dayTrips = filteredData.filter((trip) => trip.date === dateString);
    
    setSelectedDate(date);
    setSelectedDateTrips(dayTrips);
    setIsTripListModalVisible(true);
  }, [filteredData]);

  const calendarHeaderRender = useCallback(({ value, onChange }) => {
    const localeData = value.localeData();
    const months = localeData.monthsShort();
    const monthOptions = [];

    for (let i = 0; i < 12; i++) {
        monthOptions.push(
            <Select.Option key={i} value={i}>
                {months[i]}
            </Select.Option>,
        );
    }

    const year = value.year();
    const month = value.month();
    const yearOptions = [];
    for (let i = 2026; i < 2026 + 11; i += 1) {
        yearOptions.push(
            <Select.Option key={i} value={i}>
                {i}
            </Select.Option>,
        );
    }

    return (
        <div className="ouber-calendar-toolbar" style={{ padding: '8px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button 
                icon={<SyncOutlined />} 
                onClick={() => loadTrips(unitFilter)}
                title="Refresh Calendar"
            />
            <Select
                size="middle"
                dropdownMatchSelectWidth={false}
                value={year}
                style={{ width: 90 }}
                onChange={(newYear) => {
                    const now = value.clone().year(newYear);
                    onChange(now);
                }}
            >
                {yearOptions}
            </Select>
            <Select
                size="middle"
                dropdownMatchSelectWidth={false}
                value={month}
                style={{ width: 90 }}
                onChange={(newMonth) => {
                    const now = value.clone().month(newMonth);
                    onChange(now);
                }}
            >
                {monthOptions}
            </Select>
            
            <Select
                size="middle"
                showSearch
                placeholder="Search unit..."
                allowClear
                value={unitFilter || undefined}
                onChange={value => setUnitFilter(value || '')}
                style={{ width: 250 }}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={Object.keys(officeAcronyms).sort().map(key => ({
                  value: key,
                  label: `${key} - ${officeAcronyms[key]}`
                }))}
            />
        </div>
    );
  }, [unitFilter, loadTrips]);

  const handleViewTrip = useCallback((item) => {
    if (item.isUnlinkedRequest) {
      setSelectedRequest({
        ...item.rawData,
        id: item.id,
        pickup: item.pickup,
        dest: item.destination,
        date: item.date,
        time: item.time,
        status: item.status,
        purpose: item.purpose
      });
      setIsTripListModalVisible(false);
      setIsViewReqModalVisible(true);
    } else {
      setSelectedTrip(item);
      setIsTripListModalVisible(false);
      setIsViewTripModalVisible(true);
    }
  }, []);

  const handleRequestTripClick = () => {
    setIsTripListModalVisible(false);
    setIsRequestTripModalVisible(true);
  };

  const handleCancelTripClick = (tripReqId) => {
    // Find the actual request object from the selected trip's requests to populate the modal data
    const requestsArr = Array.isArray(selectedTrip?.requests) 
      ? selectedTrip.requests 
      : (typeof selectedTrip?.requests === 'object' && selectedTrip.requests !== null 
          ? Object.values(selectedTrip.requests) 
          : []);
    const req = requestsArr.find(r => r.trip_request_id === tripReqId);
    if (req) {
      setSelectedRequest({
        ...req,
        dest: req.dropoff_location,
        pickup: req.pickup_venue
      });
    }
    
    setTripRequestIdToCancel(tripReqId);
    setIsViewTripModalVisible(false);
    setIsCancelConfirmModalVisible(true);
  };

  const executeCancellation = async () => {
    if (!tripRequestIdToCancel) return;
    setCancelLoading(true);
    try {
      await ouberService.updateRequestStatus(tripRequestIdToCancel, { request_status: "Cancelled by User" });
      showSuccessToast("Success", "Request successfully cancelled!");
      setIsCancelConfirmModalVisible(false);
      loadTrips(unitFilter);
    } catch (e) {
      const errorMsg = e.response?.data?.message || "Could not cancel request.";
      showErrorToast("Error", errorMsg);
    } finally {
      setCancelLoading(false);
    }
  };


  const handleRequestJoin = async (payload) => {
    try {
      await ouberService.createTripRequest(payload);
      showSuccessToast("Success", "Trip request successfully submitted!");
      setIsViewTripModalVisible(false);
      loadTrips(unitFilter);
    } catch (e) {
      const errorMsg = e.response?.data?.message || "Could not submit trip request.";
      showErrorToast("Error", errorMsg);
      throw e;
    }
  };

  const handleUpdateMyPickup = async (tripRequestId, payload) => {
    try {
      await ouberService.updateRequestStatus(tripRequestId, payload);
      showSuccessToast("Success", "Pickup details updated!");
      loadTrips(unitFilter);
    } catch (e) {
      const errorMsg = e.response?.data?.message || "Could not update pickup details.";
      showErrorToast("Error", errorMsg);
      throw e;
    }
  };

  const handleCreateCustomRequest = async (payload) => {
    try {
      await ouberService.createTripRequest(payload);
      showSuccessToast("Success", "Custom trip request successfully submitted!");
      setIsRequestTripModalVisible(false);
      loadTrips(unitFilter);
    } catch (e) {
      const errorMsg = e.response?.data?.message || "Could not submit custom trip request.";
      showErrorToast("Error", errorMsg);
      throw e;
    }
  };

  /* --- CALENDAR RENDER LOGIC --- */
  const dateCellRender = useCallback((value) => {
    const dateString = value.format("YYYY-MM-DD");
    // Find all trips for this specific day and sort by time
    const dayTrips = filteredData
      .filter((trip) => trip.date === dateString)
      .sort((a, b) => {
        const getRawTime = (item) => item.pickup_time || item.rawData?.estimated_pickup_time || "00:00:00";
        return getRawTime(a).localeCompare(getRawTime(b));
      });

    return (
      <div className="ouber-calendar-date-cell">
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {dayTrips.map((trip) => (
            <li key={trip.id} style={{ marginBottom: "2px" }}>
              <div
                onClick={(e) => {
                  e.stopPropagation(); 
                  handleViewTrip(trip);
                }}
                className={`trip-card-block ${(() => {
                  if (trip.isUnlinkedRequest) return "legend-pending";
                  
                  const myId = Number(employeeId || currentUser?.id);
                  const userP = trip.passengers?.find(p => Number(p.id) === myId || Number(p.employee_id) === myId);
                  const isTripCompleted = trip.status === "Completed" || trip.status === "Completed Trip";

                  if (userP) {
                    const isApproved = 
                      userP.status === "Approved by AO" || 
                      userP.status === "Completed Trip" || 
                      userP.pivot?.request_status === "Approved by AO" || 
                      userP.pivot?.request_status === "Completed Trip" || 
                      userP.pivot?.is_requestor;
                    return isApproved ? "legend-confirmed" : "legend-pending";
                  }

                  if (isTripCompleted || trip.status === "In Progress") return "legend-unavailable";
                  
                  const totalQueued = trip.passengers?.length || 0;
                  const isFull = totalQueued >= (Number(trip.trip_passenger_capacity) || 0);
                  
                  const dDate = trip.deadline_date || (trip.trip_date ? dayjs(trip.trip_date).subtract(1, 'day').format('YYYY-MM-DD') : null);
                  const dTime = trip.deadline_time || "17:00:00";
                  let isDeadlinePassed = false;
                  if (dDate) {
                    const deadline = dayjs(`${dDate} ${dTime}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm a"]);
                    isDeadlinePassed = dayjs().isAfter(deadline);
                  }

                  return (isFull || isDeadlinePassed) ? "legend-unavailable" : "legend-available";
                })()}`}
              >
                <span className="trip-time" style={{ fontWeight: 600 }}>{trip.time}</span>
                <span className="trip-dest" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {trip.pickup} <ArrowRightOutlined style={{ fontSize: '8px' }} /> {trip.destination}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [filteredData, employeeId, currentUser, handleViewTrip]);

  return (
    <div className="ouber-page-container ouber-calendar-page">
      {activeView === 'trips' ? (
        <UserManageTripReqPage onBackToCalendar={() => setActiveView('calendar')} />
      ) : (
        <>
          <Space className="ouber-page-header">
            <Typography.Title level={3} className="ouber-page-title">
              Trips Calendar
              <OuberPageInfoIcon
                message="Calendar of Scheduled Trips"
                description="View all scheduled trips system-wide on the calendar below. Click on a specific day or a trip card for details."
              />
            </Typography.Title>
            <Space>
              <Button
                size="large"
                icon={<SyncOutlined />}
                onClick={handleRefresh}
                loading={refreshing}
              >
                Refresh
              </Button>
              <Button
                size="large"
                icon={<UnorderedListOutlined />}
                onClick={() => setActiveView('trips')}
              >
                My Trips
              </Button>
              <Tooltip title={!hasActivePosition ? noPositionMessage : ""}>
                <Button
                  size="large"
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  className="ouber-btn-primary"
                  style={{ marginBottom: 0 }}
                  onClick={handleRequestTripClick}
                  disabled={!hasActivePosition}
                >
                  Request Trip
                </Button>
              </Tooltip>
            </Space>
          </Space>

          <div className="ouber-calendar-legend">
            <div className="legend-item"><div className="legend-color legend-available"></div> Available</div>
            <div className="legend-item"><div className="legend-color legend-pending"></div> Requested to Join</div>
            <div className="legend-item"><div className="legend-color legend-confirmed"></div> Approved by AO</div>
            <div className="legend-item"><div className="legend-color legend-unavailable"></div> Unavailable/Full Capacity</div>
          </div>

          <div
            className={`ouber-calendar-grid ouber-calendar-weeks--${calendarWeekCount}`}
          >
            <Calendar
              className="ouber-calendar"
              value={currentDate}
              onPanelChange={(date) => setCurrentDate(date)}
              onSelect={(date, { source }) => {
                setCurrentDate(date);
                if (source === 'date') {
                  handleDayClick(date);
                }
              }}
              cellRender={dateCellRender}
              headerRender={calendarHeaderRender}
            />
          </div>
        </>
      )}

      <UserTripListModal
        isOpen={isTripListModalVisible}
        onClose={() => setIsTripListModalVisible(false)}
        selectedDate={selectedDate}
        dayTrips={selectedDateTrips}
        onViewTrip={handleViewTrip}
        onRequestTrip={handleRequestTripClick}
        hasActivePosition={hasActivePosition}
        noPositionMessage={noPositionMessage}
      />

      <UserViewTripModal
        isOpen={isViewTripModalVisible}
        onClose={() => setIsViewTripModalVisible(false)}
        tripData={selectedTrip}
        onCancelTrip={handleCancelTripClick}
        onRequestJoin={handleRequestJoin}
        onUpdateMyPickup={handleUpdateMyPickup}
        hasActivePosition={hasActivePosition}
        noPositionMessage={noPositionMessage}
      />

      <UserViewTripReqModal 
        isOpen={isViewReqModalVisible}
        onClose={() => setIsViewReqModalVisible(false)}
        requestData={selectedRequest}
        onCancelRequest={async (req) => {
          setTripRequestIdToCancel(req.id || req.trip_request_id);
          setIsViewReqModalVisible(false);
          setIsCancelConfirmModalVisible(true);
        }}
      />

      <UserCancelConfirmModal 
        isOpen={isCancelConfirmModalVisible}
        onClose={() => setIsCancelConfirmModalVisible(false)}
        onConfirm={executeCancellation}
        loading={cancelLoading}
        requestData={selectedRequest}
      />

      <UserRequestTripModal 
        isOpen={isRequestTripModalVisible}
        onClose={() => setIsRequestTripModalVisible(false)}
        initialDate={selectedDate}
        onSave={handleCreateCustomRequest}
      />
    </div>
  );
};

export default UserCalendarOverviewPage;
